import type {Ref} from "vue"
import type {InputAuthToken, OutputUserIdentity} from "~/models";
import {useApi} from "./useApi"
import * as Sentry from "@sentry/browser";
import {getDefaultPageForRole} from "~/utils/roles";

export const useAuth = () => {
    const api = useApi()
    const authState: Ref<OutputUserIdentity | null> = useState("auth")
    const isAuthLoaded: Ref<boolean> = useState('isAuthLoaded', () => false)
    const isAuthenticated: Ref<boolean> = useState('isAuthenticated', () => false)
    const currentRoutePath = useState('currentRoutePath', () => ('/'))

    const getSession = async () => {
        let statusUnauthorized = false
        const userIdentity = await api.getAuthSession()
            .catch(ctx => {
                if (ctx?.response?.status === 401) {
                    statusUnauthorized = true
                    return null
                }
            })

        if (statusUnauthorized) {
            isAuthenticated.value = false
            authState.value = null
        } else if (userIdentity) {
            if (JSON.stringify(authState.value) != JSON.stringify(userIdentity)) {
                authState.value = userIdentity
                isAuthenticated.value = true
            }

            // Set users identity info
            Sentry.setUser(userIdentity)
        }

        isAuthLoaded.value = true
    }

    const signIn = async (credentials: InputAuthToken): Promise<{requires2FA?: boolean, challenge?: string, hasWebAuthn?: boolean, preferredMethod?: string}> => {
        try {
            const response = await api.getAuthToken({request: credentials}) as any
            
            // Check if 2FA is required
            if (response && response.requires2FA) {
                return {
                    requires2FA: true,
                    challenge: response.challenge,
                    hasWebAuthn: response.hasWebAuthn,
                    preferredMethod: response.preferredMethod,
                }
            }

            await nextTick(getSession)
            return {}
        } catch (error: any) {
            // If error, check if it's a 2FA challenge response
            if (error.response?.data && typeof error.response.data === 'object' && error.response.data.requires2FA) {
                return {
                    requires2FA: true,
                    challenge: error.response.data.challenge,
                    hasWebAuthn: error.response.data.hasWebAuthn,
                    preferredMethod: error.response.data.preferredMethod,
                }
            }
            throw error
        }
    }

    const signOut = async () => {
        await api.logoutAuth()
        isAuthenticated.value = false

        Sentry.setUser(null)
    }

    const backToAdmin = async () => {
        await useApi().getAuthToken({
            request: {grantType: "admin_comeback"}
        })

        Sentry.setUser(null)
    }

    const checkNavigation = (path: string) => {

        const authPage = '/'

        if (!isAuthLoaded.value) {
            return
        }

        if (!isAuthenticated.value && path != '/') {
            navigateTo(authPage)
            return
        }

        if (isAuthenticated.value && path === '/') {
            // Використовуємо дефолтну сторінку для ролі користувача
            const defaultPage = getDefaultPageForRole(authState.value?.role)
            navigateTo(defaultPage)
            return
        }

        // if (isAuthenticated.value && authState.value && authState.value.role !== roles.ADMIN.value &&
        //     path.match(/^\/admin\//)) {
        //     navigateTo(mainPage)
        //     return
        // }

        // @todo Напевно краще по стріму передавати що у користувача закінчилась сесія, а не ддосити постійно бек
        if (isAuthenticated.value) {
            getSession()
        }
    }

    callOnce(() => {

        // Перевірка авторизації, одразу під час завантаження застосунку
        getSession()

        watch(currentRoutePath, (path) => {
            checkNavigation(path)
        })

        watch(isAuthLoaded, () => {
            checkNavigation(currentRoutePath.value)
        })
        watch(isAuthenticated, () => {
            checkNavigation(currentRoutePath.value)
        })

        setInterval(() => {
            if (isAuthenticated.value) {
                getSession()
            }
        }, 1000 * 60)
    })

    const verifyTwoFactor = async (challenge: string, method: 'webauthn' | 'totp', options?: {credential?: string, code?: string}) => {
        const api = useApi()
        // This will be available after make models is run
        const request: any = {challenge, method}
        if (options?.credential) {
            request.credential = options.credential
        }
        if (options?.code) {
            request.code = options.code
        }
        await (api as any).verifyTwoFactor({request})
        await nextTick(getSession)
    }

    return {
        signIn,
        getSession,
        isAuthenticated,
        isAuthLoaded,
        signOut,
        backToAdmin,
        verifyTwoFactor,
        user: authState as Ref<OutputUserIdentity | null>,
        currentRoutePath
    }
}